import cn from "classnames"

import { DescriptionItem, Descriptions, Text } from "@/atoms"
import { ICONS_INSIGHTS } from "@/const/insights-constants"
import { IntroducedThroughUndetermined } from "@/molecules/IntroducedThroughUndetermined"
import { ShowMore } from "@/molecules/ShowMore"
import { IntroduceThroughInsightsICell } from "@/pages/TriagePage/components/molecules/IntroduceThroughInsightsICell"
import { InsightsCell } from "../InsightsCell"

import type { TFunction } from "@/hooks/useTranslation"
import type { Item } from "@/molecules/InsightsIcons"
import type { InsightsValues } from "."

import styles from "./FixInsightsCell.module.scss"

// remediationItem only changes the wording; it carries the same value key.
export const FixInsightsCell = (t: TFunction, remediationItem?: Item) =>
  function FixInsightsCellComponent({
    id,
    remediationEffort,
    knownRemediationEffort,
    introducedThroughInsights,
    someIntroducedThroughInsightsExist,
    allFalse,
    ...values
  }: InsightsValues) {
    const items = [
      remediationItem ?? ICONS_INSIGHTS.hasKodemRemediation,
      ICONS_INSIGHTS.hasFixVersion
    ]
    const emptyValue = "-"
    const trueCount = Object.values(values).filter((value) => value).length

    const descriptionItems: DescriptionItem[] = [
      {
        label: (
          <Descriptions.Label>
            {t("general.fixTypes", { count: trueCount })}
          </Descriptions.Label>
        ),
        children: allFalse ? (
          t("remediation.noFixAvailable")
        ) : (
          <InsightsCell tag t={t} items={items} values={values} />
        )
      },
      {
        label: (
          <Descriptions.Label>
            {t("general.remediationEffort")}
          </Descriptions.Label>
        ),
        children: knownRemediationEffort
          ? t(`remediation.effort.${remediationEffort}`)
          : emptyValue
      },
      {
        label: (
          <Descriptions.Label>
            {t("general.introducedThrough")}
          </Descriptions.Label>
        ),
        children: someIntroducedThroughInsightsExist ? (
          <span className={styles.insightsContainer}>
            {IntroduceThroughInsightsICell({ tag: true, t })({
              ...introducedThroughInsights
            })}
          </span>
        ) : (
          <IntroducedThroughUndetermined />
        )
      }
    ]

    return (
      <ShowMore
        id={id}
        label={
          <ShowMore.Label.Component>
            {allFalse && (
              <Text underline type="secondary">
                {t("remediation.noFixAvailable")}
              </Text>
            )}
            {!allFalse && (
              <InsightsCell tag compact t={t} items={items} values={values} />
            )}
          </ShowMore.Label.Component>
        }
      >
        <ShowMore.Component title={t("general.fixInsights")}>
          <Descriptions
            grid
            classes={{
              grid: cn(styles.gridContainer, remediationItem && styles.gridWide)
            }}
            items={descriptionItems}
          />
        </ShowMore.Component>
      </ShowMore>
    )
  }
